import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzModalModule } from 'ng-zorro-antd/modal';
import {
  NzNotificationModule,
  NzNotificationService,
} from 'ng-zorro-antd/notification';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { HasPermissionDirective } from '@/app/features/access/directives/has-permition.diirective';
import { AvatarComponent } from '@/app/common/components/avatar/avatar.component';
import { AuthService } from '@/app/modules/auth/core/service';
import { User } from '@/app/modules/user/core/type';
import { LoaderService } from '@/app/common/services/loader/loader.service';
import { VehicleRevisionService } from '@/app/modules/vehicle-revision/core/service';
import {
  REVISION_INTERVAL_KM,
  RevisionStatusEnum,
  VehicleRevisionDetail,
} from '@/app/modules/vehicle-revision/core/type';
import {
  exportRevisionDetailPdf,
  loadRevisionPdfLogo,
  RevisionPdfLogo,
} from '../../utils/revision-pdf.util';

@Component({
  templateUrl: './detail.component.html',
  styleUrls: ['./detail.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzCardModule,
    NzEmptyModule,
    NzGridModule,
    NzIconModule,
    NzInputNumberModule,
    NzLayoutModule,
    NzModalModule,
    NzNotificationModule,
    NzSpinModule,
    NzTableModule,
    NzTabsModule,
    NzTagModule,
    NzProgressModule,
    NzDividerModule,
    HasPermissionDirective,
    AvatarComponent,
  ],
})
export class VehicleRevisionDetailComponent implements OnInit, OnDestroy {
  detail: VehicleRevisionDetail | null = null;
  loading = false;
  saving = false;

  showConfigureModal = false;
  showIntervalModal = false;
  showRegisterModal = false;

  configureLastRevisionKm = 0;
  configureIntervalKm = REVISION_INTERVAL_KM;
  intervalKm = REVISION_INTERVAL_KM;
  registerRevisionKm = 0;

  RevisionStatusEnum = RevisionStatusEnum;
  currentUser: User | null = null;
  pdfLogo: RevisionPdfLogo = { base64: null, width: 0, height: 0 };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private vehicleRevisionService: VehicleRevisionService,
    private notification: NzNotificationService,
    private loaderService: LoaderService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.authService.authenticatedUser.subscribe((user) => {
      this.currentUser = user;
    });

    loadRevisionPdfLogo('assets/images/square-logo.png').then(
      (logo) => (this.pdfLogo = logo),
    );

    this.route.paramMap.subscribe((params) => {
      const chassis = params.get('chassis');
      if (chassis) {
        this.loadDetail(chassis);
      }
    });
  }

  ngOnDestroy(): void {}

  loadDetail(chassis: string): void {
    this.loading = true;
    this.loaderService
      .executeWithLoader(this.vehicleRevisionService.findByChassis(chassis))
      .subscribe({
        next: (detail) => {
          this.applyDetail(detail);
          this.loading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.loading = false;
          this.notification.error(
            `${error.status}`,
            error.error?.message || 'Erro ao carregar revisão.',
          );
          this.router.navigate(['/revisions']);
        },
      });
  }

  private applyDetail(detail: VehicleRevisionDetail): void {
    this.detail = detail;
    this.configureLastRevisionKm = detail.current_km;
    this.configureIntervalKm =
      detail.revision_interval_km ?? REVISION_INTERVAL_KM;
    this.intervalKm = detail.revision_interval_km ?? REVISION_INTERVAL_KM;
    this.registerRevisionKm = detail.current_km;
  }

  goBack(): void {
    this.router.navigate(['/revisions']);
  }

  exportToPdf(): void {
    if (!this.detail) return;

    exportRevisionDetailPdf({
      detail: this.detail,
      generatedBy: this.currentUser?.name || 'Desconhecido',
      logo: this.pdfLogo,
      getStatusLabel: (status) => this.getStatusLabel(status),
      getKmHighlightLabel: () => this.getKmHighlightLabel(),
      getKmHighlightValue: () => this.getKmHighlightValue(),
    });
  }

  openConfigureModal(): void {
    if (!this.detail) return;
    this.configureLastRevisionKm = this.detail.current_km;
    this.configureIntervalKm =
      this.detail.revision_interval_km ?? REVISION_INTERVAL_KM;
    this.showConfigureModal = true;
  }

  openIntervalModal(): void {
    if (!this.detail) return;
    this.intervalKm = this.detail.revision_interval_km ?? REVISION_INTERVAL_KM;
    this.showIntervalModal = true;
  }

  openRegisterModal(): void {
    if (!this.detail) return;
    this.registerRevisionKm = this.detail.current_km;
    this.showRegisterModal = true;
  }

  saveConfigure(): false {
    if (!this.detail || this.saving) return false;

    this.saving = true;
    this.vehicleRevisionService
      .configure(this.detail.chassis, {
        last_revision_km: this.configureLastRevisionKm,
        revision_interval_km: this.configureIntervalKm,
      })
      .subscribe({
        next: (detail) => {
          this.saving = false;
          this.showConfigureModal = false;
          this.applyDetail(detail);
          this.notification.success(
            'Configurado',
            `Revisão de ${detail.chassis} configurada.`,
          );
        },
        error: (error: HttpErrorResponse) => {
          this.saving = false;
          this.notification.error(
            `${error.status}`,
            error.error?.message || 'Erro ao configurar revisão.',
          );
        },
      });
    return false;
  }

  saveInterval(): false {
    if (!this.detail || this.saving) return false;

    this.saving = true;
    this.vehicleRevisionService
      .updateInterval(this.detail.chassis, {
        revision_interval_km: this.intervalKm,
      })
      .subscribe({
        next: (detail) => {
          this.saving = false;
          this.showIntervalModal = false;
          this.applyDetail(detail);
          this.notification.success('Intervalo atualizado', 'Salvo com sucesso.');
        },
        error: (error: HttpErrorResponse) => {
          this.saving = false;
          this.notification.error(
            `${error.status}`,
            error.error?.message || 'Erro ao atualizar intervalo.',
          );
        },
      });
    return false;
  }

  registerRevision(): false {
    if (!this.detail || this.saving) return false;

    this.saving = true;
    this.vehicleRevisionService
      .registerRevision(this.detail.chassis, {
        revision_km: this.registerRevisionKm,
      })
      .subscribe({
        next: (detail) => {
          this.saving = false;
          this.showRegisterModal = false;
          this.applyDetail(detail);
          this.notification.success(
            'Revisão registrada',
            `${detail.chassis} atualizado.`,
          );
        },
        error: (error: HttpErrorResponse) => {
          this.saving = false;
          this.notification.error(
            `${error.status}`,
            error.error?.message || 'Erro ao registrar revisão.',
          );
        },
      });
    return false;
  }

  getStatusLabel(status: RevisionStatusEnum): string {
    switch (status) {
      case RevisionStatusEnum.OVERDUE:
        return 'Pendente';
      case RevisionStatusEnum.NOT_CONFIGURED:
        return 'Não configurado';
      default:
        return 'Em dia';
    }
  }

  getStatusColor(status: RevisionStatusEnum): string {
    switch (status) {
      case RevisionStatusEnum.OVERDUE:
        return 'error';
      case RevisionStatusEnum.NOT_CONFIGURED:
        return 'warning';
      default:
        return 'success';
    }
  }

  getRevisionProgress(): number {
    if (!this.detail?.is_configured) return 0;
    const interval = this.detail.revision_interval_km ?? 0;
    if (interval <= 0) return 0;

    const traveled =
      this.detail.current_km - (this.detail.last_revision_km ?? 0);
    return Math.min(100, Math.round((traveled / interval) * 100));
  }

  getProgressStrokeColor(): string {
    if (!this.detail) return '#c0142c';
    if (this.detail.revision_status === RevisionStatusEnum.OVERDUE) {
      return '#cf1322';
    }
    if (this.detail.revision_status === RevisionStatusEnum.NOT_CONFIGURED) {
      return '#faad14';
    }
    return '#52c41a';
  }

  getKmHighlightLabel(): string {
    if (!this.detail?.is_configured) return 'Configuração';
    return this.detail.revision_status === RevisionStatusEnum.OVERDUE
      ? 'Em atraso'
      : 'Faltam para revisão';
  }

  getKmHighlightValue(): string {
    if (!this.detail) return '-';
    if (!this.detail.is_configured) return 'Pendente';
    if (this.detail.revision_status === RevisionStatusEnum.OVERDUE) {
      const overdue =
        this.detail.current_km - (this.detail.next_revision_km ?? 0);
      return `${overdue.toLocaleString('pt-BR')} km`;
    }
    return `${(this.detail.km_until_revision ?? 0).toLocaleString('pt-BR')} km`;
  }

  getKmHighlightSubtext(): string {
    if (!this.detail?.is_configured) {
      return 'Defina a última revisão e o intervalo';
    }
    if (this.detail.revision_status === RevisionStatusEnum.OVERDUE) {
      return 'Ultrapassou a quilometragem prevista';
    }
    return `Próxima em ${(this.detail.next_revision_km ?? 0).toLocaleString('pt-BR')} km`;
  }

  getHeroCardClass(): string {
    if (!this.detail) return 'hero-card';
    switch (this.detail.revision_status) {
      case RevisionStatusEnum.OVERDUE:
        return 'hero-card hero-card--overdue';
      case RevisionStatusEnum.NOT_CONFIGURED:
        return 'hero-card hero-card--pending';
      default:
        return 'hero-card hero-card--ok';
    }
  }
}
